import React, {memo, useCallback, useEffect, useMemo, useState} from 'react';
import {onClickHandler} from '../../../utils/onclick';
import iconDown from '../../../assets/images/icons/settings/down.png';
import iconUp from '../../../assets/images/icons/settings/up.png';
import './index.scss';
import {useSearchTerm} from '../../../hooks/search-term';
import { useLocalStorage } from '../../../hooks/use-storage';
import { useWindowSize } from '../../../hooks/window-size';

const typedMemo: <T>(c: T) => T = memo;

export type ColumnModel = {
    length: number;
    name: string;
    key: string;
};

const MIN_COL_WIDTH = 60;

type SortType = { column: ColumnModel; direction: 'ASC' | 'DES' };

type ListItemProps<T> = {
    index?: number;
    columns: ColumnModel[];
    item?: T;
    selected?: boolean;
    displayNameSelector: (item: T, column: ColumnModel, index?: number) => string;
    onClick?: () => void;
    className?: string;
    sort?: SortType;
    onColumnClick?: (column: ColumnModel) => void;
    columnWidths: number[];
    onResizeStart?: (e: React.MouseEvent | React.TouchEvent, columnIndex: number) => void;
};

const ListItem = typedMemo(function ListItem<T>(props: ListItemProps<T>) {
    const {
        columns, selected, onClick, className,
        displayNameSelector, item, index, sort,
        onColumnClick,
        columnWidths,
        onResizeStart,
    } = props;

    return (
        <div className={'line ' + className + (selected ? ' selected' : '')} {...onClickHandler(onClick)}>
            {/*<span>{index === -1 ? 'Sıra' : (index == null ? '-' : index + 1)}</span>*/}
            {
                columns.map((column, i) => (
                    <span
                        style={{flex: `0 1 ${columnWidths[i]}px`, minWidth: `${MIN_COL_WIDTH}px`}}
                        {...onClickHandler(index === -1 && sort ? () => onColumnClick(column) : null)}
                        key={`row_${i}`}
                    >
                        {displayNameSelector(item, column, index)}
                        {
                            renderIf(sort && sort.column === column && sort.direction)(() =>
                                renderIf(sort.direction === 'ASC')(
                                    () => <img className="small" src={iconUp} alt="up"/>,
                                    () => <img className="small" src={iconDown} alt="down"/>,
                                ))
                        }
                        {renderIf(index === -1 && i !== columns.length - 1 && onResizeStart)(() => (
                            <div
                                className="resize-handle"
                                onMouseDown={e => onResizeStart(e, i)}
                                onTouchStart={e => onResizeStart(e, i)}
                            />
                        ))}
                    </span>
                ))
            }
        </div>
    );
});

export type SettingsListProps<T> = {
    columns: ColumnModel[];
    listType?: string
    items: T[];
    selectedItem: T;
    onItemSelected: (item: T) => void;
    keyExtractor: (item: Partial<T>) => string;
    classExtractor?: (item: Partial<T>) => string;
    displayNameExtractor?: (item: T, column: ColumnModel, index?: number) => string;
    notSelectableItems?: Partial<T>[];
    sortDisabled?: boolean;
};

const headerSelector = (item: any, column: ColumnModel) => column.name;

export const SettingsList = typedMemo(
    function SettingsList<T>(props: SettingsListProps<T>) {
        const {
            columns, items, selectedItem, onItemSelected,
            keyExtractor, classExtractor, notSelectableItems,
            displayNameExtractor, sortDisabled, listType
        } = props;

        const searchTerm = useSearchTerm();

        const [width, height] = useWindowSize();
        const isPc = useMemo(() => {
            return !((width === 1024 && height === 600) || (height === 1024 && width === 600));
        }, [width, height]);

        // ---------------- Resizable columns state ----------------
        const DEFAULT_WIDTH_UNIT = 120; 
        const [columnWidths, setColumnWidths] = useState<number[]>(
            columns.map(col => col.length * DEFAULT_WIDTH_UNIT),
        );

       
        useEffect(() => {
            setColumnWidths(columns.map(col => col.length * DEFAULT_WIDTH_UNIT));
        }, [columns]);

        const onResizeStart = useCallback(
            (e: React.MouseEvent | React.TouchEvent, columnIndex: number) => {
                if (!isPc) return; 
                e.preventDefault();

                const startX = 'touches' in e ? (e.touches as any)[0].clientX : (e as any).clientX;
                const startWidths = [...columnWidths];

                const handleMove = (moveEvent: MouseEvent | TouchEvent) => {
                    const clientX = 'touches' in moveEvent ? (moveEvent as TouchEvent).touches[0].clientX : (moveEvent as MouseEvent).clientX;
                    const delta = clientX - startX;

                    setColumnWidths(prev => {
                        const next = [...prev];
                        next[columnIndex] = Math.max(MIN_COL_WIDTH, startWidths[columnIndex] + delta);
                        return next;
                    });
                };

                const handleUp = () => {
                    window.removeEventListener('mousemove', handleMove);
                    window.removeEventListener('mouseup', handleUp);
                    window.removeEventListener('touchmove', handleMove);
                    window.removeEventListener('touchend', handleUp);
                };

                window.addEventListener('mousemove', handleMove);
                window.addEventListener('mouseup', handleUp);
                window.addEventListener('touchmove', handleMove);
                window.addEventListener('touchend', handleUp);
            },
            [columnWidths, isPc],
        );
        // -------------------------------------------------------------------

        const [sort, setSort] = useLocalStorage<SortType>(listType, {column: columns[0], direction: "DES"})

        const onColumnClick = useCallback((column: ColumnModel) => {
            if (sortDisabled) {
                return;
            }

            if (sort.column !== column) {
                setSort({column: column, direction: 'ASC'});
                return;
            }

            setSort({
                ...sort,
                direction: sort.direction === 'ASC' ? 'DES' : 'ASC',
            });
        }, [sort, setSort, sortDisabled]);

        const displayNameSelector = useMemo(() => {
            if (displayNameExtractor) {
                return displayNameExtractor;
            }

            return (item: T, column: ColumnModel) => {
                return item[column.key] || '-';
            };
        }, [displayNameExtractor]);

        const filteredSortedItems = useMemo(() => {
            if (!sort) {
                return items;
            }

            const diff = sort.direction === 'ASC' ? 1 : -1;
            const getSortValue = (item: T) => {
                const displayValue: any = displayNameSelector(item, sort.column);
                const rawValue: any = (item as any)[sort.column.key];
                const value = typeof displayValue === 'string' ? displayValue : rawValue;
                return (value ?? '').toString();
            };
            return [...items]
                .filter(item => searchTerm == null ? true :
                    columns.reduce((acc: string, cur: ColumnModel) =>
                        `${acc} ${displayNameSelector(item, cur)}`, '').toLowerCase().includes(searchTerm.toLowerCase()))
                .sort(((a, b) => getSortValue(a).localeCompare(getSortValue(b), 'tr', {numeric: true}) * diff));
        }, [columns, displayNameSelector, items, searchTerm, sort]);

        useEffect(() => {
            if (sortDisabled) {
                setSort(null)
            }
        }, [sortDisabled, setSort])

        return (
            <div className="settings-list-component">
                <ListItem
                    index={-1}
                    columns={columns}
                    displayNameSelector={headerSelector}
                    sort={sort}
                    onColumnClick={onColumnClick}
                    columnWidths={columnWidths}
                    onResizeStart={isPc ? onResizeStart : undefined}
                />
                {
                    renderIf(notSelectableItems)(() => notSelectableItems.map(item => (
                        <ListItem key={keyExtractor(item)} columns={columns} item={item}
                                  displayNameSelector={displayNameSelector}
                                  columnWidths={columnWidths}
                        />
                    )))
                }
                {
                    filteredSortedItems.map((item, index) => (
                        <ListItem
                            index={index}
                            key={`row_${keyExtractor(item)}`}
                            columns={columns}
                            item={item}
                            className={classExtractor ? classExtractor(item) : ''}
                            selected={selectedItem === item}
                            onClick={() => onItemSelected(item)}
                            displayNameSelector={displayNameSelector}
                            columnWidths={columnWidths}
                        />
                    ))
                }
            </div>
        );
    },
);

export const SettingsListEnhanced = SettingsList;